import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { FaAmazon } from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router';

import { getProductBySlug } from '../../data/products';
import OptimizedImage from '../OptimizedImage';
import JoinSection from '../sections/JoinSection';
import WorkbookDownloadPopup from '../WorkbookDownloadPopup';

const ProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);

  const product = useMemo(
    () => (slug ? getProductBySlug(slug) : undefined),
    [slug],
  );

  if (!product) {
    return (
      <section className="section bg-surface dark:bg-panel/90">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-primary dark:text-theme-primary mb-4">
              Product not found
            </h1>
            <p className="text-theme-secondary mb-8">
              We couldn't find the product you're looking for.
            </p>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-primary"
            >
              Go back
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="section bg-surface dark:bg-panel/90">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <Link to="/" className="text-secondary font-medium hover:underline">
              ← Back to home
            </Link>
          </motion.div>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-card dark:bg-panel/80 rounded-lg shadow-surface/60 overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative bg-card dark:bg-panel h-full">
                <OptimizedImage
                  src={product.detailImage || product.image}
                  alt={product.title}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
              </div>
              <div className="p-8 space-y-6">
                <div>
                  <span className="uppercase tracking-wide text-xs font-semibold text-secondary">
                    {product.category}
                  </span>
                  <h1 className="text-3xl md:text-4xl font-bold text-primary dark:text-theme-primary mt-2">
                    {product.title}
                  </h1>
                </div>
                <p className="text-lg text-theme-secondary">
                  {product.description}
                </p>

                {/* Text-based Achievement Display */}
                {product.award && (
                  <div className="space-y-2 mb-6">
                    <a
                      href="https://www.bookmarketingprofits.com/PinnacleAwardsFall2025.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm font-semibold text-brand-secondary hover:text-brand-primary transition-colors duration-200"
                    >
                      Pinnacle Book Achievement Award Winner
                    </a>
                    <a
                      href="https://www.amazon.com/Konnect-Better-Unlocking-Relationships-Connections/dp/B0FV14D5V1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm font-semibold text-brand-secondary hover:text-brand-primary transition-colors duration-200"
                    >
                      Amazon Best Seller
                    </a>
                    <a
                      href="https://independentbookreview.com/2026/02/12/konnect-better-by-mo-lidsky-phd/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm font-semibold text-brand-secondary hover:text-brand-primary transition-colors duration-200"
                    >
                      Independent Book Review
                    </a>
                  </div>
                )}

                <div>
                  {product.id === 1 ? (
                    <>
                      <motion.a
                        href="https://a.co/d/drPmmD8"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-block"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Order Now
                      </motion.a>
                      <div className="mt-6">
                        <p className="text-sm text-theme-secondary mb-3">
                          Also available at:
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <motion.a
                            href="https://a.co/d/drPmmD8"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-12 h-12 rounded-full bg-card dark:bg-panel border border-border hover:border-secondary hover:bg-secondary/10 transition-all duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            title="Amazon"
                          >
                            <FaAmazon className="w-6 h-6 text-primary dark:text-theme-primary" />
                          </motion.a>
                          <motion.a
                            href="https://www.barnesandnoble.com/w/konnect-better-mo-lidsky-phd/1148503636"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-12 h-12 rounded-full bg-card dark:bg-panel border border-border hover:border-secondary hover:bg-secondary/10 transition-all duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            title="Barnes & Noble"
                          >
                            <span className="text-xs font-bold text-primary dark:text-theme-primary">
                              B&N
                            </span>
                          </motion.a>
                          <motion.a
                            href="https://www.indigo.ca/products/konnect-better-unlocking-the-power-of-your-relationships-volume-one-committed-connections?variant=46646021193938"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-12 h-12 rounded-full bg-card dark:bg-panel border border-border hover:border-secondary hover:bg-secondary/10 transition-all duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            title="Indigo"
                          >
                            <span className="text-xs font-bold text-primary dark:text-theme-primary">
                              indigo
                            </span>
                          </motion.a>
                          <motion.a
                            href="https://www.walmart.com/ip/Konnect-Better-Unlocking-the-Power-of-Your-Relationships-Volume-One-Committed-Connections-Hardcover-9798891385795/18134763639"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-12 h-12 rounded-full bg-card dark:bg-panel border border-border hover:border-secondary hover:bg-secondary/10 transition-all duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            title="Walmart"
                          >
                            <span className="text-xs font-bold text-primary dark:text-theme-primary">
                              W
                            </span>
                          </motion.a>
                        </div>
                      </div>
                    </>
                  ) : product.downloadConfig ? (
                    <motion.button
                      onClick={() => setIsDownloadPopupOpen(true)}
                      className="btn-primary inline-flex items-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FiDownload className="w-5 h-5" />
                      Download Free Workbook
                    </motion.button>
                  ) : (
                    <a
                      href="/joincommunity"
                      className="btn-primary inline-block"
                    >
                      Join the Community
                    </a>
                  )}
                </div>
                {product.detailedDescription && (
                  <div className="space-y-4 text-theme-secondary leading-relaxed">
                    {product.detailedDescription
                      .split('\n\n')
                      .map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </motion.article>
        </div>
      </section>
      <div className="-mt-8 md:-mt-12">
        <JoinSection key={slug} useInitialAnimation={true} />
      </div>

      <WorkbookDownloadPopup
        isOpen={isDownloadPopupOpen}
        onClose={() => setIsDownloadPopupOpen(false)}
        product={product}
      />
    </>
  );
};

export default ProductDetailPage;
